/**
 * Host allowlist for pasting Passport / IdP URLs into the Citi SSO iframe preview.
 * Defaults cover OE Passport frontends; add Citi (or other) hosts via
 * REACT_APP_CITI_SSO_IFRAME_ALLOWED_HOST_SUFFIXES=comma,separated,suffixes
 * (each entry matches the hostname if it equals the suffix or ends with ".suffix").
 */
const DEFAULT_HOST_SUFFIXES = [
  ".open-exchange.net",
  ".dev-open-exchange.net",
  ".stage-open-exchange.net",
];

function normalizeSuffix(entry) {
  const t = entry.trim().toLowerCase();
  if (!t) return null;
  return t.startsWith(".") ? t : `.${t}`;
}

export function getAllowedHostSuffixes() {
  const fromEnv = (process.env.REACT_APP_CITI_SSO_IFRAME_ALLOWED_HOST_SUFFIXES || "")
    .split(",")
    .map(normalizeSuffix)
    .filter(Boolean);
  return [...new Set([...DEFAULT_HOST_SUFFIXES, ...fromEnv])];
}

function hostMatchesSuffix(hostname, suffixWithDot) {
  const host = hostname.toLowerCase();
  const suf = suffixWithDot.toLowerCase();
  const root = suf.startsWith(".") ? suf.slice(1) : suf;
  return host === root || host.endsWith(suf);
}

function isLoopbackHost(hostname) {
  const h = hostname.toLowerCase();
  return h === "localhost" || h === "127.0.0.1";
}

export function validateCitiSsoIframeUrl(raw) {
  const trimmed = (raw || "").trim();
  if (!trimmed) {
    return { ok: false, error: "Enter a Passport SSO login URL." };
  }

  let url;
  try {
    url = new URL(trimmed);
  } catch {
    return { ok: false, error: "URL must be a valid http(s) address." };
  }

  const host = url.hostname;

  if (url.protocol === "http:") {
    if (!isLoopbackHost(host)) {
      return {
        ok: false,
        error: "Only https URLs are allowed (http is permitted for localhost only).",
      };
    }
    return { ok: true, url: url.toString() };
  }

  if (url.protocol !== "https:") {
    return { ok: false, error: "URL must use https:// (or http:// on localhost)." };
  }

  if (isLoopbackHost(host)) {
    return { ok: true, url: url.toString() };
  }

  const suffixes = getAllowedHostSuffixes();
  const allowed = suffixes.some((suf) => hostMatchesSuffix(host, suf));
  if (!allowed) {
    return {
      ok: false,
      error:
        "This host is not allowed. Use an approved Passport/SSO URL, or ask your team to add the host via REACT_APP_CITI_SSO_IFRAME_ALLOWED_HOST_SUFFIXES.",
    };
  }

  return { ok: true, url: url.toString() };
}
